import {assert} from 'Asserts';
import {BenchlingFunctionSignature} from 'benchling-expressions/BenchlingFunctionSignatures';
import {
  isBenchlingTypeLeafType,
  BenchlingTypeDef,
  LeafType,
} from 'data-types/benchling-type-def/BenchlingTypeDef';
import inferTypeFromLookupConfig, {InferredLookupConfigType} from 'lookup-config/inferTypeFromLookupConfig';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import {Schema} from 'schemas/SchemaConstants';

import {
  getAllowedRoutingOperatorsForValueType,
  isSupportedRouterLookupType,
  LookupReturnType,
} from './RouterConfigConstants';

/**
 * Finds the operators and constant value which are compatible with a lookup config which is the "left hand"
 * parameter of a comparison.
 *
 * Note that we expect comparisons to be of the form:
 * ```
 * <lookup> <operator> <constant value>
 * ```
 * where we can determine a set of operators and the type of the constant value based on the type returned by
 * the lookup config.
 *
 * @param lookupConfig The lookup config that is the left hand parameter of the function
 * @param baseSchema The schema of the object the lookup is starting from
 * @param allSchemasById A map of ID to schema
 * @returns All operators which are compatible with the lookup config and the expected type of the right hand
 * parameter of the function.
 */
export function getAllowedOperatorAndValueInfo(
  lookupConfig: LookupConfigSpec,
  baseSchema: Schema
): {
  isMultiplex: boolean;
  allowedOperators: Array<BenchlingFunctionSignature>;
  selectorId: string | undefined;
  typeDef: BenchlingTypeDef;
} {
  const inferredTypeDef = inferTypeFromLookupConfig(lookupConfig, baseSchema);
  return getAllowedOperatorAndValueInfoFromInferredType(inferredTypeDef);
}

export function getAllowedOperatorAndValueInfoFromInferredType(inferredTypeDef: InferredLookupConfigType): {
  isMultiplex: boolean;
  allowedOperators: Array<BenchlingFunctionSignature>;
  selectorId: string | undefined;
  typeDef: BenchlingTypeDef;
} {
  const {isMultiplex, typeDef, linkSelectorId} = inferredTypeDef;
  if (typeDef === LeafType.DROPDOWN_OPTION) {
    assert(linkSelectorId !== undefined, 'lookup must return valid lookup type');
  }

  assert(
    isBenchlingTypeLeafType(typeDef) && isSupportedRouterLookupType(typeDef),
    'lookup must return valid lookup type'
  );

  const operatorsForMultiplexity = getAllowedOperatorsForTypeDef(typeDef, isMultiplex);

  assert(operatorsForMultiplexity != null, 'lookup must return valid lookup type', {
    typeDef,
    isMultiplex,
  });

  return {
    isMultiplex,
    allowedOperators: operatorsForMultiplexity,
    selectorId: typeDef === LeafType.DROPDOWN_OPTION ? linkSelectorId : undefined,
    typeDef,
  };
}

export function hasAllowedOperatorsForInferredType(inferredTypeDef: InferredLookupConfigType): boolean {
  const {isMultiplex, typeDef} = inferredTypeDef;
  if (!isBenchlingTypeLeafType(typeDef) || !isSupportedRouterLookupType(typeDef)) {
    return false;
  }

  return getAllowedOperatorsForTypeDef(typeDef, isMultiplex) !== undefined;
}

export function getAllowedOperatorsForTypeDef(
  typeDef: LeafType,
  isMultiplex: boolean
): Array<BenchlingFunctionSignature> | undefined {
  const allowedOperators = getAllowedRoutingOperatorsForValueType(typeDef);
  if (!allowedOperators) {
    return undefined;
  }
  return allowedOperators[isMultiplex ? LookupReturnType.MULTIPLE_VALUES : LookupReturnType.SINGLE_VALUE];
}
